import { formatOrderMessage } from '@/helpers/orderHelper';
import { LuceraBlotterItem } from '@/model/blotters';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import { generatePair } from '@/state/reducers/marketPairSlice';
import { LumeFxAmendOrderRequest, LumeFxOrderType, LumeFxSide, LumeFxTimeInForce } from '@/types/lumeFx';
import { getCurrency, useCurrency } from '@/utils/hooks/useCurrency';
import { useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import useMinOrderSize from '@/utils/hooks/useMinOrderSize';
import useOrderBook, { invalidLimitPriceTooltip, invalidStopPriceMessage } from '@/utils/hooks/useOrderBook';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import InstrumentInfo, { formConfig } from '../common/InstrumentInfo';
import Tooltip from '../common/Tooltip';
import FormBody from '../form/components/FormBody';
import FormFooter from '../form/components/FormFooter';
import { durationOptions, orderTypeOptions } from '../form/OcoOrderForm';
import { AmendOCOOrderFormInput, amendOCOOrderSchema } from '../form/schema/amendOCOOrderSchema';
import ExecutionInputWrapper from '../inputs/ExecutionInputWrapper';
import Input from '../inputs/Input';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

interface LuceraAmendOcoOrderModalProps extends ModalProps {
    listId: string;
    orders: {
        order1: LuceraBlotterItem;
        order2: LuceraBlotterItem;
    };
    duration?: any;
    onCancelOrder: (order, order2) => void;
}

const defaultValues: AmendOCOOrderFormInput = {
    order1: {
        price: 0,
        quantity: 0,
        stopPrice: 0
    },
    order2: {
        price: 0,
        quantity: 0,
        stopPrice: 0
    }
};

const isCcy2Order = false;
const luceraRestURL = window.config?.lucera?.rest;

const LuceraAmendOcoOrderModal = (props: LuceraAmendOcoOrderModalProps) => {
    const { listId, orders, duration, opened, handlers, onCancelOrder } = props;
    const { order1, order2 } = orders;
    const { orderType, side } = order1;

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const activePair = useMemo(() => order1.pair, [orders]);
    const market = useMemo(() => generatePair(activePair), [activePair]);

    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);

    const [ccy1, ccy2] = useMemo(() => activePair.split('/'), [activePair]);

    const sideState = useState<LumeFxSide>(LumeFxSide[order1.side]);

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const instrument = useInstrument(activePair);
    const { pip_size, formatPrice } = instrument;

    const orderBook = useOrderBook(activePair, sideState[0], isCcy2Order);
    const { bestAsk, bestBid } = orderBook;

    const activeCurrencyConfig = useMemo(() => ccy1Config, [ccy1Config]);

    const priceCurrency = useMemo(() => {
        if (['Index', 'Commodity', 'Future'].includes(instrument?.type || '')) {
            return getCurrency(instrument.config.ccy2 || '');
        } else return ccy2Config;
    }, [instrument, ccy2Config]);

    const orderMessage = useMemo(
        () =>
            formatOrderMessage({
                side: sideState[0],
                isCcy2Order,
                ccy1Config,
                ccy2Config,
                pairMapToUse: market,
                instrumentConfig: instrument
            }),
        [sideState[0], isCcy2Order, ccy1Config, ccy2Config, market, instrument]
    );

    const { orderDecimals, minOrderSize } = useMinOrderSize({ instrument, activeCurrencyConfig });

    const [locked, setLocked] = useState<boolean>(false);

    const form = useForm<AmendOCOOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            amendOCOOrderSchema(
                {
                    side,
                    orderType,
                    bestAsk,
                    bestBid,
                    formattedBestAsk: formatPrice(bestAsk),
                    formattedBestBid: formatPrice(bestBid),
                    ccy2Order: isCcy2Order,
                    order_decimals: orderDecimals,
                    min_order_size: minOrderSize,
                    currencyPair: activePair,
                    currencyOut: activeCurrencyConfig.show
                },
                {
                    side: LumeFxSide[side],
                    bestAsk,
                    bestBid,
                    formattedBestAsk: formatPrice(bestAsk),
                    formattedBestBid: formatPrice(bestBid),
                    ccy2Order: isCcy2Order
                }
            )
        )
    });

    const {
        reset,
        watch,
        setError,
        clearErrors,
        handleSubmit,
        formState: { isDirty, isSubmitting, isValid }
    } = form;

    const [formOrder1, formOrder2] = watch(['order1', 'order2']);

    const isRestingLimitOrder = useMemo(() => {
        if (duration) return ['DAY', 'GTC'].includes(duration);
        return false;
    }, [duration]);

    const limitPriceTooltip = useMemo(() => {
        let tooltip: any = '';
        if (isRestingLimitOrder && formOrder1?.price && bestAsk && bestBid) {
            if (side === 'BUY') {
                if (isCcy2Order && formOrder1.price < bestBid) {
                    tooltip = invalidLimitPriceTooltip(LumeFxSide[side], bestBid, isCcy2Order);
                } else if (!isCcy2Order && formOrder1.price > bestAsk) {
                    tooltip = invalidLimitPriceTooltip(LumeFxSide[side], bestAsk, isCcy2Order);
                }
            }
            if (side === 'SELL') {
                if (isCcy2Order && formOrder1.price > bestAsk) {
                    tooltip = invalidLimitPriceTooltip(LumeFxSide[side], bestAsk, isCcy2Order);
                } else if (!isCcy2Order && formOrder1.price < bestBid) {
                    tooltip = invalidLimitPriceTooltip(LumeFxSide[side], bestBid, isCcy2Order);
                }
            }
        }
        return tooltip;
    }, [bestAsk, bestBid, side, formOrder1?.price, isRestingLimitOrder]);

    useEffect(() => {
        const isCcy1Buy = side === 'BUY' && !isCcy2Order;
        const isCcy2Buy = side === 'BUY' && isCcy2Order;
        const isCcy1Sell = side === 'SELL' && !isCcy2Order;
        const isCcy2Sell = side === 'SELL' && isCcy2Order;

        if (formOrder2?.stopPrice && bestBid && bestAsk) {
            if (isCcy1Buy || isCcy2Sell) {
                const message = invalidStopPriceMessage(LumeFxSide[side], formatPrice(bestBid), isCcy2Order);
                if (+formOrder2.stopPrice <= bestBid) {
                    setError('order2.stopPrice', { type: 'stopPriceConstraint', message });
                }
            } else if (isCcy2Buy || isCcy1Sell) {
                const message = invalidStopPriceMessage(LumeFxSide[side], formatPrice(bestAsk), isCcy2Order);
                if (+formOrder2.stopPrice >= bestAsk) {
                    setError('order2.stopPrice', { type: 'stopPriceConstraint', message });
                }
            } else {
                clearErrors('order2.stopPrice');
            }
        }
    }, [bestBid, bestAsk, formOrder2?.stopPrice, isCcy2Order, side]);

    const onSubmit = async (data: AmendOCOOrderFormInput, e) => {
        const { order1, order2 } = data as AmendOCOOrderFormInput;
        if (orders && credentials) {
            setLocked(true);

            const order1Payload: LumeFxAmendOrderRequest = {
                Side: LumeFxSide[side],
                Symbol: orders.order1.pair,
                Account: orders.order1.account,
                OrdType: LumeFxOrderType[orders.order1.orderType],
                ClOrdID: crypto.randomUUID(),
                OrigClOrdID: orders.order1.clientOrderId,
                TimeInForce: LumeFxTimeInForce[orders.order1.tif],
                // amended fields
                OrderQty: (order1?.quantity || 0).toString(),
                Price: (order1?.price || 0).toString()
            };

            await lumeFxRestClient.amendOrder(order1Payload);
            const order2Payload: LumeFxAmendOrderRequest = {
                Side: LumeFxSide[side],
                Symbol: orders.order2.pair,
                Account: orders.order2.account,
                OrdType: LumeFxOrderType[orders.order2.orderType],
                ClOrdID: crypto.randomUUID(),
                OrigClOrdID: orders.order2.clientOrderId,
                TimeInForce: LumeFxTimeInForce[orders.order2.tif],
                // amended fields
                OrderQty: (order2?.quantity || 0).toString(),
                StopPx: (order2?.stopPrice || 0).toString()
            };

            if (orders.order2.orderType === 'StopLimit') {
                order2Payload['Price'] = (order2?.price || 0).toString();
            }
            await lumeFxRestClient.amendOrder(order2Payload);
            handlers.close();

            setLocked(false);
        }
    };

    const onError = (errors) => console.error(errors);

    const resetDefault = useCallback(() => {
        const resetValues: typeof defaultValues = {
            order1: {
                ...defaultValues.order1,
                price: order1?.price || 0,
                quantity: order1?.qty || 0,
                stopPrice: order1?.stopPx || 0
            } as any,
            order2: {
                ...defaultValues.order2,
                price: order2?.price || 0,
                quantity: order2?.qty || 0,
                stopPrice: order2?.stopPx || 0
            } as any
        };

        reset(resetValues);
    }, [defaultValues, side, order1, order2]);

    useEffect(() => {
        if (opened) resetDefault();
    }, [activePair, opened]);

    return (
        <Modal {...props} className="lg:h-auto" size="max-w-xl">
            <ModalHeader>
                <ModalTitle>Amend OCO Order</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider />
            <FormProvider {...form}>
                <form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    className="relative flex h-full w-full text-brand-text pb-14">
                    <FormBody mode="Modal" side={LumeFxSide[order1.side]}>
                        <div className="flex items-center gap-1 text-sm md:text-base">
                            <span className="whitespace-nowrap">{orderMessage}</span>
                            <InstrumentInfo
                                market={{
                                    show: activePair,
                                    celer: activePair,
                                    netdania: activePair
                                }}
                                {...formConfig}
                            />
                        </div>
                        <div className="flex flex-wrap gap-3 mt-3">
                            <AmendOCOOrderForm
                                side={side}
                                title="Order 1"
                                idPrefix="order1"
                                order={orders.order1}
                                pip_size={pip_size}
                                min_order_size={minOrderSize}
                                step={activeCurrencyConfig.increments}
                                currency={activeCurrencyConfig.show}
                                priceCurrency={priceCurrency}
                                limitPriceTooltip={limitPriceTooltip}
                                isRestingLimitOrder={isRestingLimitOrder}
                            />
                            <AmendOCOOrderForm
                                side={side}
                                title="Order 2"
                                idPrefix="order2"
                                order={orders.order2}
                                pip_size={pip_size}
                                min_order_size={minOrderSize}
                                step={activeCurrencyConfig.increments}
                                currency={activeCurrencyConfig.show}
                                priceCurrency={priceCurrency}
                                isRestingLimitOrder={isRestingLimitOrder}
                            />
                        </div>
                        <ExecutionInputWrapper label={`${lang.orderForm.duration} *`}>
                            <Select
                                type="execution"
                                options={durationOptions}
                                selected={durationOptions.find((option) => option.label === duration)}
                                disabled
                            />
                        </ExecutionInputWrapper>
                    </FormBody>
                    <FormFooter>
                        <div className="flex flex-row justify-between text-brand-text text-sm items-center gap-2 p-2">
                            <button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600 hidden sm:block"
                                onClick={handlers.close}>
                                Back
                            </button>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    className="rounded-md p-2 px-4 focus:ring-brand-red-light !bg-brand-red hover:!bg-brand-red-light w-auto"
                                    onClick={() => onCancelOrder(order1, order2)}>
                                    Cancel Order
                                </Button>
                                <Button
                                    type="submit"
                                    className="rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light w-auto"
                                    isLoading={isSubmitting || locked}
                                    disabled={!isDirty || !isValid || locked}>
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    </FormFooter>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default LuceraAmendOcoOrderModal;

export const AmendOCOOrderForm = ({
    side,
    title,
    idPrefix,
    order,
    pip_size,
    min_order_size,
    step,
    currency,
    priceCurrency,
    limitPriceTooltip,
    isRestingLimitOrder
}: any) => {
    const type = order.orderType;
    const isStopType = ['StopLimit', 'Stop'].includes(type);
    const isLimitType = ['StopLimit', 'Limit'].includes(type);

    const lang = useLanguage();
    const { register, watch, setValue } = useFormContext();

    const slippage = useMemo(() => order?.metadata?.find((data) => data.key === 'SLIPPAGE')?.value, [order]);

    const [order1Quantity, stopPrice] = watch([`order1.quantity`, 'order2.stopPrice']);

    const filteredOrderTypeOptions = useMemo(() => {
        if (idPrefix === 'order2') {
            return orderTypeOptions.filter((option) => option.value !== LumeFxOrderType.Limit);
        } else return orderTypeOptions;
    }, [idPrefix]);

    const stopLimitPrice = useMemo(() => {
        if (idPrefix === 'order2' && type === 'StopLimit' && stopPrice) {
            const slip = BigNumber(+(slippage || 0));
            const stop = +(stopPrice || 0);
            if (side === 'BUY') return BigNumber(stop).plus(slip).toNumber();
            if (side === 'SELL') return BigNumber(stop).minus(slip).toNumber();
            return stop;
        }
        return 0;
    }, [stopPrice, side, slippage]);

    useEffect(() => {
        if (idPrefix === 'order2' && order1Quantity) {
            setValue('order2.quantity', order1Quantity, { shouldValidate: true });
        }
    }, [order1Quantity]);

    useEffect(() => {
        setValue(`order2.price`, stopLimitPrice, { shouldValidate: true });
    }, [stopLimitPrice]);

    return (
        <div className="relative flex-1 flex flex-col gap-3 p-2 border border-brand-neutral-700 rounded-md">
            <div className="-top-2 text-xs absolute bg-brand-background text-brand-neutral-400 px-1">{title}</div>
            <ExecutionInputWrapper label="Order Id">
                <Input placeholder="Order Id" value={order.orderId} disabled />
            </ExecutionInputWrapper>
            <ExecutionInputWrapper label="Status">
                <Input placeholder="Status" value={order.status} disabled />
            </ExecutionInputWrapper>
            <ExecutionInputWrapper label={`${lang.orderForm.orderType} *`}>
                <Select
                    type="execution"
                    options={filteredOrderTypeOptions}
                    selected={filteredOrderTypeOptions.find((option) => option.value === LumeFxOrderType[type])}
                    disabled
                />
            </ExecutionInputWrapper>
            {isStopType && (
                <RHFNumberInput
                    label={`${lang.orderForm.stopPrice} *`}
                    placeholder={lang.orderForm.stopPrice}
                    {...register(`${idPrefix}.stopPrice`)}
                    step={BigNumber(1)
                        .dividedBy(Math.pow(10, pip_size || 0))
                        .toNumber()}
                    currency={priceCurrency.show}
                />
            )}
            {isLimitType && (
                <Tooltip content={limitPriceTooltip || ''} className="-top-16 right-0 w-2/3">
                    <RHFNumberInput
                        label={`${lang.orderForm.limitPrice} *`}
                        placeholder={lang.orderForm.limitPrice}
                        {...register(`${idPrefix}.price`)}
                        disabled={!isRestingLimitOrder}
                        className={{
                            'text-brand-red': limitPriceTooltip,
                            'text-brand-text': !limitPriceTooltip
                        }}
                        step={BigNumber(1)
                            .dividedBy(Math.pow(10, pip_size || 0))
                            .toNumber()}
                        currency={priceCurrency.show}
                    />
                </Tooltip>
            )}
            <RHFNumberInput
                {...register(`${idPrefix}.quantity`)}
                label={`${lang.orderForm.quantity} *`}
                placeholder={lang.orderForm.quantity}
                min={min_order_size}
                step={step}
                disabled={idPrefix === 'order2'}
                currency={currency}
            />
        </div>
    );
};
